import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Alert,
} from '@mui/material';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../contexts/AuthContext';
import { Terminal, TerminalStatus, TerminalSensitivity } from '../../../types/terminal';

// Site interface (defined locally since it's not exported from a types file)
interface Site {
  id: string;
  name: string;
  address: string;
  photo?: string;
  masterOwnerID: string;
  createdAt: any;
  devices?: any[];
  terminals?: Terminal[];
  description?: string;
  contactPerson?: string;
  contactPhone?: string;
}

interface AddTerminalModalProps {
  open: boolean;
  onClose: () => void;
  site: Site | null;
  onTerminalAdded: () => void;
}

const terminalTypes = [
  'ATM',
  'Card Reader',
  'POS Terminal',
  'Payment Kiosk',
  'Self-Service Terminal',
  'Other'
];

const sensitivityOptions: TerminalSensitivity[] = ['low', 'medium', 'high'];

// Helper function to convert frequency to duration in seconds
const getFrequencyDuration = (frequency: string): number => {
  switch (frequency) {
    case 'daily': return 30;
    case 'weekly': return 60;
    case 'bi-weekly': return 90;
    case 'monthly': return 120;
    default: return 30;
  }
};

const AddTerminalModal: React.FC<AddTerminalModalProps> = ({
  open,
  onClose,
  site,
  onTerminalAdded,
}) => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    terminalType: '',
    makeModel: '',
    readerModel: '',
    serialNumber: '',
    locationDescription: '',
    coordinates: { x: 0, y: 0 },
    tags: [] as string[],
    scanFrequency: 'daily',
    sensitivity: 'medium' as TerminalSensitivity,
    status: 'unscanned' as TerminalStatus,
  });
  const [newTag, setNewTag] = useState('');

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async () => {
    if (!site || !userProfile) {
      setError('Site or user information is missing');
      return;
    }

    if (!formData.name.trim() || !formData.terminalType || !formData.makeModel.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const terminalData: Omit<Terminal, 'id'> = {
        name: formData.name.trim(),
        terminalType: formData.terminalType,
        makeModel: formData.makeModel.trim(),
        readerModel: formData.readerModel.trim(),
        serialNumber: formData.serialNumber.trim(),
        siteLocation: {
          description: formData.locationDescription.trim(),
          coordinates: formData.coordinates,
        },
        tags: formData.tags,
        scanDefaults: {
          duration: getFrequencyDuration(formData.scanFrequency),
          sensitivity: formData.sensitivity,
        },
        status: formData.status,
        siteId: site.id,
        masterOwnerID: userProfile.masterOwnerID,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addDoc(collection(db, 'devices'), {
        ...terminalData,
        // Store as legacy device format for compatibility
        specification: formData.locationDescription.trim(),
        modelName: formData.makeModel.trim(),
        deviceType: formData.terminalType,
        modelNumber: formData.readerModel.trim(),
        qrCodeData: formData.serialNumber.trim(),
        isActive: formData.status === 'clean',
        terminalData: {
          name: formData.name.trim(),
          serialNumber: formData.serialNumber.trim(),
          tags: formData.tags,
          scanDefaults: {
            duration: getFrequencyDuration(formData.scanFrequency),
            sensitivity: formData.sensitivity,
          },
          status: formData.status,
        },
      });

      onTerminalAdded();
      handleClose();
    } catch (err) {
      console.error('Error adding terminal:', err);
      setError('Failed to add terminal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      terminalType: '',
      makeModel: '',
      readerModel: '',
      serialNumber: '',
      locationDescription: '',
      coordinates: { x: 0, y: 0 },
      tags: [],
      scanFrequency: 'daily',
      sensitivity: 'medium',
      status: 'unscanned',
    });
    setNewTag('');
    setError(null);
    onClose();
  };

  const paperSx = {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(34, 211, 238, 0.25)',
    borderTop: '1px solid rgba(34, 211, 238, 0.5)',
    borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
    borderRadius: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
  };
  const titleSx = {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '0.3em',
    textTransform: 'uppercase' as const,
    padding: '20px 24px',
    borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
    backgroundColor: 'transparent',
  };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={titleSx}>
        Add Payment Terminal to {site?.name}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mt: 1 }}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Terminal Name *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Main Entrance ATM"
            />
            </Box>
            <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Terminal Type *</InputLabel>
              <Select
                value={formData.terminalType}
                onChange={(e) => handleInputChange('terminalType', e.target.value)}
                label="Terminal Type *"
              >
                {terminalTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            </Box>
          </Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Make/Model *"
              value={formData.makeModel}
              onChange={(e) => handleInputChange('makeModel', e.target.value)}
              placeholder="e.g., NCR SelfServ 80"
            />
            </Box>
            <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Reader Model"
              value={formData.readerModel}
              onChange={(e) => handleInputChange('readerModel', e.target.value)}
              placeholder="e.g., Ingenico iCT250"
            />
            </Box>
          </Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Serial Number"
              value={formData.serialNumber}
              onChange={(e) => handleInputChange('serialNumber', e.target.value)}
              placeholder="Device serial number"
            />
            </Box>
            <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="unscanned">Unscanned</MenuItem>
                <MenuItem value="clean">Clean</MenuItem>
                <MenuItem value="possible_risk">Possible Risk</MenuItem>
                <MenuItem value="under_investigation">Under Investigation</MenuItem>
              </Select>
            </FormControl>
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Location Description"
              value={formData.locationDescription}
              onChange={(e) => handleInputChange('locationDescription', e.target.value)}
              placeholder="e.g., Near main entrance, next to customer service"
              multiline
              rows={2}
            />
          </Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
               <InputLabel>Scan Frequency</InputLabel>
               <Select
                 value={formData.scanFrequency}
                 onChange={(e) => handleInputChange('scanFrequency', e.target.value)}
                 label="Scan Frequency"
               >
                 <MenuItem value="daily">Daily</MenuItem>
                 <MenuItem value="weekly">Weekly</MenuItem>
                 <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                 <MenuItem value="monthly">Monthly</MenuItem>
               </Select>
             </FormControl>
            </Box>
            <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Sensitivity</InputLabel>
              <Select
                value={formData.sensitivity}
                onChange={(e) => handleInputChange('sensitivity', e.target.value)}
                label="Sensitivity"
              >
                {sensitivityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Tags
              </Typography>
              <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                  />
                ))}
              </Box>
              <Box display="flex" gap={1}>
                <TextField
                  size="small"
                  label="Add tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddTag}
                  disabled={!newTag.trim()}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="outlined"
          disabled={loading || !formData.name.trim() || !formData.terminalType || !formData.makeModel.trim()}
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(34, 211, 238, 0.5)',
            background: 'rgba(34, 211, 238, 0.15)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
            '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.05)' },
          }}
        >
          {loading ? 'Adding...' : 'Add Terminal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTerminalModal;